import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { MapPin, Users, Calendar } from 'lucide-react';
import { thematicSpaces, regionalSpaces } from '../data/plansData';

const EcosystemSpaces = () => {
  const [activeTab, setActiveTab] = useState('thematic');

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName] || LucideIcons.Sparkles;
    return Icon;
  };

  return (
    <section 
      id="spaces"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="ecosystem-spaces"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-surface-dark via-surface to-surface-dark" />
      
      {/* Decorative elements */}
      <motion.div
        className="absolute top-0 right-0 w-96 h-96 rounded-full bg-neon-green/5 blur-[100px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gold/40 bg-gold/10 text-gold text-sm font-semibold mb-6">
            <Users className="w-4 h-4" />
            Espaços do Ecossistema
          </span>
          <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
            Encontre <span className="gradient-text-green">Sua Tribo</span>
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            Dentro do ecossistema há{' '}
            <span className="text-neon-green font-semibold">inúmeros espaços temáticos</span> e{' '}
            <span className="text-gold font-semibold">grupos regionais</span> onde você conecta com quem realmente combina com você.
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="flex items-center justify-center gap-2 mb-12"
        >
          <div className="inline-flex items-center gap-1 p-1.5 bg-surface border border-white/10 rounded-full">
            <button
              onClick={() => setActiveTab('thematic')}
              className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all ${
                activeTab === 'thematic'
                  ? 'bg-neon-green text-black'
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="tab-thematic"
            >
              Espaços Temáticos
            </button>
            <button
              onClick={() => setActiveTab('regional')}
              className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all ${
                activeTab === 'regional'
                  ? 'bg-gold text-black'
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="tab-regional"
            >
              Espaços Regionais
            </button>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeTab === 'thematic' ? (
            <motion.div
              key="thematic"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {thematicSpaces.map((space, index) => {
                const Icon = getIcon(space.icon);
                const isGold = space.color === 'gold';
                return (
                  <motion.div
                    key={space.name}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className={`group relative p-6 rounded-2xl bg-surface border transition-all overflow-hidden cursor-pointer ${
                      isGold 
                        ? 'border-white/10 hover:border-gold/50' 
                        : 'border-white/10 hover:border-neon-green/50'
                    }`}
                    whileHover={{ y: -8, scale: 1.02 }}
                    data-testid={`thematic-space-${index}`}
                  >
                    {/* Hover glow */}
                    <motion.div
                      className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${
                        isGold
                          ? 'bg-gradient-to-t from-gold/10 to-transparent'
                          : 'bg-gradient-to-t from-neon-green/10 to-transparent'
                      }`}
                    />

                    <div className="relative z-10">
                      <div className="flex items-start justify-between mb-4">
                        <motion.div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center border transition-colors ${
                            isGold
                              ? 'bg-gold/10 border-gold/30 group-hover:bg-gold/20'
                              : 'bg-neon-green/10 border-neon-green/30 group-hover:bg-neon-green/20'
                          }`}
                          whileHover={{ rotate: [0, -10, 10, 0] }}
                          transition={{ duration: 0.5 }}
                        >
                          <Icon className={`w-6 h-6 ${isGold ? 'text-gold' : 'text-neon-green'}`} />
                        </motion.div>
                        <div className="flex items-center gap-1 text-white/50 text-xs">
                          <Users className="w-3 h-3" />
                          {space.members}
                        </div>
                      </div>

                      <h3 className="font-outfit font-bold text-lg text-white mb-2">
                        {space.name}
                      </h3>
                      <p className="text-white/60 text-sm leading-relaxed">
                        {space.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key="regional"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            >
              {regionalSpaces.map((space, index) => (
                <motion.div
                  key={space.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative p-5 rounded-xl bg-surface border border-white/10 hover:border-gold/50 transition-all overflow-hidden cursor-pointer"
                  whileHover={{ y: -5, scale: 1.03 }}
                  data-testid={`regional-space-${index}`}
                >
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-gold/10 to-transparent transition-opacity pointer-events-none"
                  />

                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <motion.div
                        className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center"
                        whileHover={{ scale: 1.1 }}
                      >
                        <MapPin className="w-4 h-4 text-gold" />
                      </motion.div>
                      <h3 className="font-outfit font-bold text-white">
                        {space.name}
                      </h3>
                    </div>
                    <div className="flex items-center justify-between text-xs text-white/50">
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        <span>{space.members}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{space.meetups}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-3 gap-6 max-w-3xl mx-auto"
        >
          {[
            { value: '25+', label: 'Espaços temáticos' },
            { value: '12+', label: 'Cidades ativas' },
            { value: '10K+', label: 'Membros conectados' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              className="text-center p-6 rounded-2xl bg-surface/50 border border-white/10"
              whileHover={{ scale: 1.05, borderColor: 'rgba(0, 255, 0, 0.3)' }}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <p className="font-outfit font-black text-3xl gradient-text-green mb-1">
                {stat.value}
              </p>
              <p className="text-white/60 text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default EcosystemSpaces;
